import { extractHtmlError, fetchWithXsrf, getAntiForgeryToken } from "@siisel/frontend-shared";
import { useState } from "react";
import type { LoginFormViewModel } from "../types";

export default function LoginFormApp(props: LoginFormViewModel) {
  const [email, setEmail] = useState(props.values.email);
  const [password, setPassword] = useState(props.values.password);
  const [rememberMe, setRememberMe] = useState(props.values.rememberMe);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(props.initialError ?? null);

  async function submit(root: HTMLElement | null) {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const formData = new FormData();
      const token = getAntiForgeryToken(root);
      if (token) {
        formData.append("__RequestVerificationToken", token);
      }

      formData.append("Email", email);
      formData.append("Password", password);
      formData.append("RememberMe", rememberMe ? "true" : "false");
      formData.append("submit", props.submitText);

      const response = await fetchWithXsrf(props.submitUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "text/html" },
        body: formData
      });

      if (response.redirected) {
        window.location.href = response.url;
        return;
      }

      const html = await response.text();
      if (!response.ok) {
        throw new Error(html);
      }

      const validationMessage = extractHtmlError(html);
      if (validationMessage) {
        setErrorMessage(validationMessage);
        return;
      }

      window.location.reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Prihlaseni se nepodarilo.";
      setErrorMessage(message.includes("<") ? extractHtmlError(message) || message : message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="forms-app login-form-app">
      {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}
      <div className="base-form">
        <div className="form-group login-username">
          <input
            className="form-control"
            value={email}
            disabled={isSubmitting}
            placeholder={props.labels.emailPlaceholder}
            onChange={(event) => {
              setEmail(event.currentTarget.value);
              setErrorMessage(null);
            }}
          />
        </div>
        <div className="form-group login-password">
          <input
            className="form-control"
            type="password"
            value={password}
            disabled={isSubmitting}
            placeholder={props.labels.passwordPlaceholder}
            onChange={(event) => {
              setPassword(event.currentTarget.value);
              setErrorMessage(null);
            }}
          />
        </div>
        <div>
          <input
            id="login-remember-me"
            type="checkbox"
            checked={rememberMe}
            disabled={isSubmitting}
            onChange={(event) => {
              setRememberMe(event.currentTarget.checked);
              setErrorMessage(null);
            }}
          />
          <label htmlFor="login-remember-me">{props.labels.rememberMe}</label>
        </div>
        <div>
          <button
            type="button"
            className="btn btn-block btn-lg btn-primary"
            disabled={isSubmitting}
            onClick={(event) => void submit(event.currentTarget.closest(".forms-app"))}
          >
            {isSubmitting ? `${props.submitText}...` : props.submitText}
          </button>
        </div>
      </div>
    </div>
  );
}
